import { defineStore } from 'pinia'
import { ref } from 'vue'
import { walletAPIServiceV2 } from '@/networks/api/v2/wallet-api.service'
import type { Maybe } from '@/types'

const useAccountBalanceAPIStore = defineStore('accountBalanceAPI', () => {
  const accountBalanceList = ref<Record<string, string>>({})
  const accountTokenBalanceList = ref<
    Record<string, Maybe<Record<string, string>>>
  >({})

  /**
   * 여러 account의 balance를 한 번에 조회
   */
  const fetchAccountBalances = async (accountIds: string[]) => {
    if (!accountIds || accountIds.length === 0) return

    try {
      // V2: /wallet/account/balance API 호출 (여러 accountId 한 번에)
      const balanceRes = await walletAPIServiceV2.getAccountBalances(accountIds)

      // V2 응답: { accounts: [{ id, balance, parentAccountId?, accountClassName, ... }] }
      // 백엔드는 요청한 accountIds 만 RPC 로 조회하므로, 토큰 잔액을 받으려면 토큰 계정 ID 를 함께 보내야 한다
      if (balanceRes?.accounts && Array.isArray(balanceRes.accounts)) {
        for (const account of balanceRes.accounts) {
          const accountId = account.id
          const balance = account.balance || '0'

          // 토큰 계정은 AccountToken 이 읽는 [부모 계정 ID][토큰 계정 ID] 위치에 저장한다 (WV2-650)
          if (
            account.accountClassName === 'TokenAccount' &&
            account.parentAccountId
          ) {
            accountTokenBalanceList.value[account.parentAccountId] = {
              ...accountTokenBalanceList.value[account.parentAccountId],
              [accountId]: balance,
            }
            continue
          }
          accountBalanceList.value[accountId] = balance
        }
      }
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * 단일 account의 balance 조회 (하위 호환용)
   */
  const fetchAccountBalance = async ({
    accountId,
    force = false,
  }: {
    accountId: string
    force?: boolean
  }) => {
    if (!force && accountBalanceList.value[accountId]) {
      return
    }
    await fetchAccountBalances([accountId])
  }

  const reset = () => {
    accountBalanceList.value = {}
    accountTokenBalanceList.value = {}
  }

  return {
    accountBalanceList,
    accountTokenBalanceList,
    fetchAccountBalance,
    fetchAccountBalances,
    reset,
  }
})

export { useAccountBalanceAPIStore }
